import * as THREE from 'three';

export const GUARDIAN_POSITION = new THREE.Vector3(0, 2, -10.4);

// Every guardian is built from lit, solid geometry in the board's world.
// Shared fractured cores connect the silhouettes to the Glitch Core's design.
export const createGuardianScene = (scene) => {
  const root = new THREE.Group();
  scene.add(root);
  let identity = null;
  let body;
  let heart;
  let ring;
  let shards = [];
  let materials = [];
  const build = (enemy) => {
    root.traverse((object) => {
      object.geometry?.dispose();
      if (object.material) object.material.dispose();
    });
    root.clear();
    shards = [];
    const color = new THREE.Color(enemy.color);
    const shell = new THREE.MeshStandardMaterial({
      color: '#162938',
      metalness: 0.75,
      roughness: 0.3,
      emissive: color,
      emissiveIntensity: 0.12,
      flatShading: true,
    });
    const facet = new THREE.MeshStandardMaterial({
      color: color.clone().multiplyScalar(0.5),
      metalness: 0.6,
      roughness: 0.27,
      emissive: color,
      emissiveIntensity: 0.3,
      flatShading: true,
    });
    const light = new THREE.MeshStandardMaterial({
      color: '#e9ffff',
      emissive: color,
      emissiveIntensity: 2.5,
      roughness: 0.15,
      metalness: 0.2,
    });
    materials = [shell, facet, light];
    body = new THREE.Group();
    root.add(body);
    const part = (
      geometry,
      position,
      scale = [1, 1, 1],
      material = shell,
      rotation = [0, 0, 0]
    ) => {
      const mesh = new THREE.Mesh(geometry, material);
      mesh.position.set(...position);
      mesh.scale.set(...scale);
      mesh.rotation.set(...rotation);
      mesh.castShadow = true;
      mesh.userData.home = mesh.position.clone();
      mesh.userData.homeScale = mesh.scale.clone();
      mesh.userData.escape = mesh.position
        .clone()
        .normalize()
        .multiplyScalar(2.2);
      if (mesh.userData.escape.lengthSq() < 0.1)
        mesh.userData.escape.set(0.4, 0.8, 0.3);
      body.add(mesh);
      shards.push(mesh);
      return mesh;
    };
    const rock = (position, scale, material = shell) =>
      part(new THREE.IcosahedronGeometry(1, 0), position, scale, material);
    const spike = (position, scale, rotation = [0, 0, 0]) =>
      part(
        new THREE.ConeGeometry(0.5, 1.7, 4),
        position,
        scale,
        facet,
        rotation
      );
    const limb = (from, to, thickness = 0.11) => {
      const a = new THREE.Vector3(...from),
        b = new THREE.Vector3(...to);
      const mesh = part(
        new THREE.CylinderGeometry(
          thickness * 0.7,
          thickness,
          a.distanceTo(b),
          5
        ),
        a.clone().add(b).multiplyScalar(0.5).toArray(),
        [1, 1, 1],
        facet
      );
      mesh.quaternion.setFromUnitVectors(
        new THREE.Vector3(0, 1, 0),
        b.sub(a).normalize()
      );
    };
    const blade = (side) => {
      const shape = new THREE.Shape();
      shape.moveTo(0, 0.5);
      shape.lineTo(side * 2.2, 1.4);
      shape.lineTo(side * 1.8, -0.3);
      shape.lineTo(side * 0.5, -0.7);
      shape.closePath();
      part(
        new THREE.ExtrudeGeometry(shape, {
          depth: 0.22,
          bevelEnabled: true,
          bevelSize: 0.07,
          bevelThickness: 0.05,
          bevelSegments: 1,
        }),
        [side * 0.3, 0, -0.3],
        [1, 1, 1],
        facet
      );
    };
    rock([0, 0, 0], [1, 1.25, 0.65]);
    rock([-0.44, 0.45, 0.45], [0.6, 0.7, 0.32], facet);
    rock([0.46, -0.3, 0.4], [0.5, 0.65, 0.3], facet);
    const kind = enemy.shape;
    if (kind === 'wing') {
      blade(-1);
      blade(1);
      spike([0, -1.3, 0], [0.5, 0.7, 0.5], [0, 0, Math.PI]);
    } else if (kind === 'spider') {
      rock([0, -0.55, -0.25], [0.9, 1.05, 0.8]);
      [-1, 1].forEach((s) =>
        [-0.75, -0.25, 0.3, 0.75].forEach((y, i) => {
          const knee = [s * (1.5 + i * 0.13), y + 0.5, -0.2];
          limb([s * 0.65, y, 0], knee);
          limb(knee, [s * (2.25 - i * 0.13), y - 0.9, 0.3]);
        })
      );
    } else if (kind === 'serpent') {
      for (let i = 0; i < 7; i++) {
        const t = i / 6;
        rock(
          [Math.sin(t * 4.5) * 1.25, -t * 2.3, -0.3 - t * 0.15],
          [0.55 - t * 0.24, 0.55 - t * 0.2, 0.5 - t * 0.2],
          i % 2 ? facet : shell
        );
      }
      spike([-0.65, 0.85, 0.1], [0.6, 0.7, 0.5], [0, 0, 0.5]);
      spike([0.65, 0.85, 0.1], [0.6, 0.7, 0.5], [0, 0, -0.5]);
    } else if (kind === 'hound') {
      rock([0, -0.6, -0.4], [1.45, 0.65, 0.8]);
      [-1, 1].forEach((s) => {
        limb([s * 0.75, -0.4, 0.4], [s * 1.3, -1.6, 0.6], 0.2);
        limb([s * 0.85, -0.4, -0.65], [s * 1, -1.4, -0.85], 0.16);
        spike([s * 0.65, 1, 0], [0.7, 0.8, 0.5], [0, 0, -s * 0.5]);
      });
    } else if (kind === 'maw') {
      rock([0, -0.8, 0.1], [1.35, 0.55, 0.8]);
      for (let i = -2; i <= 2; i++)
        spike([i * 0.4, -0.36, 0.7], [0.28, 0.32, 0.3], [0, 0, Math.PI]);
      rock([0, 0.6, -0.2], [1.3, 0.65, 0.7]);
    } else if (kind === 'reaper') {
      spike([0, -1.2, 0], [1.8, 1.2, 1], [0, 0, Math.PI]);
      limb([-1, 0.2, 0], [-1.6, -0.3, 0.3], 0.15);
      limb([-1.6, -1.6, 0.3], [-1.6, 1.7, 0.3], 0.08);
      part(
        new THREE.TorusGeometry(0.8, 0.1, 5, 13, Math.PI * 1.2),
        [-1, 1.5, 0.3],
        [1, 1, 1],
        light
      );
      spike([0.95, -0.1, 0], [0.7, 1.1, 0.5], [0, 0, 0.6]);
    } else if (kind === 'warden' || kind === 'knight' || kind === 'sentry') {
      [-1, 1].forEach((s) => {
        rock([s * 1.15, 0.15, 0], [0.6, 0.8, 0.7], facet);
        rock([s * 1.55, -0.55, 0.1], [0.4, 0.7, 0.55]);
        spike([s * 0.55, -1.25, 0], [0.7, 0.9, 0.6], [0, 0, Math.PI]);
      });
      if (kind === 'warden') {
        for (let i = -2; i <= 2; i++)
          spike(
            [i * 0.42, 1.25 + (i % 2 === 0 ? 0.2 : 0), 0],
            [0.65, 0.9, 0.5],
            [0, 0, -i * 0.17]
          );
        root.scale.setScalar(1.12);
      } else if (kind === 'knight') {
        limb([1.4, -0.6, 0.2], [2, 1.1, 0.2], 0.1);
        spike([2, 1.3, 0.2], [0.4, 0.9, 0.25]);
        part(
          new THREE.BoxGeometry(0.8, 1.45, 0.2),
          [-1.5, -0.5, 0.7],
          [1, 1, 1],
          facet,
          [0, 0.2, -0.15]
        );
      } else {
        spike([-0.6, 1, 0], [0.7, 0.6, 0.6]);
        spike([0.6, 1, 0], [0.7, 0.6, 0.6]);
      }
    } else {
      for (let i = 0; i < 4; i++)
        rock(
          [Math.sin(i * 2) * 0.8, -1 - i * 0.25, 0],
          [0.35, 0.5, 0.3],
          facet
        );
    }
    heart = part(
      new THREE.OctahedronGeometry(0.42),
      [0, 0.14, 0.84],
      [1, 1, 0.55],
      light,
      [0, 0, Math.PI / 4]
    );
    part(
      new THREE.BoxGeometry(0.08, 0.38, 0.05),
      [0, 0.14, 1.08],
      [1, 1, 1],
      light
    );
    ring = new THREE.Mesh(new THREE.TorusGeometry(1.7, 0.025, 5, 42), facet);
    ring.rotation.x = 1.15;
    ring.position.y = -0.1;
    root.add(ring);
    identity = enemy.id;
  };
  return {
    root,
    update({
      enemy,
      elapsed,
      reduceMotion,
      hit = 0,
      death = 0,
      retaliation = 0,
    }) {
      root.visible = !!enemy;
      if (!enemy) return;
      if (identity !== enemy.id) {
        root.scale.setScalar(1);
        build(enemy);
        root.scale.multiplyScalar(1.3);
      }
      root.position.copy(GUARDIAN_POSITION);
      root.position.y += reduceMotion ? 0 : Math.sin(elapsed * 1.8) * 0.16;
      root.rotation.y = reduceMotion ? -0.12 : Math.sin(elapsed * 0.65) * 0.18;
      body.rotation.z = reduceMotion ? 0 : Math.sin(elapsed * 1.1) * 0.035;
      body.position.x =
        hit && !reduceMotion ? Math.sin(hit * 52) * 0.16 * (1 - hit) : 0;
      body.position.z = reduceMotion ? 0 : retaliation * 0.4;
      body.rotation.x = reduceMotion ? 0 : -retaliation * 0.12;
      materials[0].emissiveIntensity =
        0.12 + retaliation * 0.8 + Math.max(0, 1 - hit * 3) * (hit > 0 ? 2 : 0);
      materials[1].emissiveIntensity = 0.3 + retaliation;
      materials[2].emissiveIntensity = 2.5 + retaliation * 2;
      ring.rotation.z = reduceMotion ? 0 : elapsed * 0.22;
      ring.scale.setScalar(1 + retaliation * 0.3);
      shards.forEach((mesh) => {
        mesh.position.copy(mesh.userData.home);
        mesh.scale.copy(mesh.userData.homeScale);
        if (enemy.shape === 'warden' && retaliation && !reduceMotion) {
          mesh.position.x += mesh.userData.home.x * retaliation * 0.1;
          if (Math.abs(mesh.userData.home.x) > 1)
            mesh.position.y += retaliation * 0.45;
        }
        if (death > 0) {
          mesh.position.addScaledVector(mesh.userData.escape, death * 2.5);
          mesh.scale.multiplyScalar(Math.max(0.01, 1 - death));
        }
      });
      heart.scale.multiplyScalar(1 + retaliation * 0.6);
      if (death >= 1 || (reduceMotion && death > 0)) root.visible = false;
    },
  };
};
